/**
 * Utility functions for the PeriodSelector.
 * Pure functions — no React dependencies, no side effects.
 */

/** Maps a parent level to its child level for virtual-period fetching. */
export const CHILD_LEVEL_MAP: Record<string, string> = {
  root: 'year',
  year: 'quarter',
  quarter: 'month',
  month: 'week',
};

/* ------------------------------------------------------------------------- */
/*  Timeframe info accepted by getLevels                                     */
/* ------------------------------------------------------------------------- */

/** A single level within a timeframe nesting chain. */
export interface TimeframeNestingLevel {
  timeframeTypeKey: string;
  nestedTimeframes?: TimeframeNestingLevel[];
}

/** Minimal shape needed to extract levels from a timeframe. */
export interface TimeframeLevelInfo {
  timeframeTypeKey?: string;
  scheduleDefinition?: { rrule?: string; type?: string } | null;
  nestedTimeframes?: TimeframeNestingLevel[];
}

/**
 * Extract the FREQ value from an RRULE string.
 * Returns `'YEARLY'` as a fallback if the string is empty or has no FREQ param.
 *
 * @example parseRRuleFrequency('FREQ=YEARLY;INTERVAL=1') → 'YEARLY'
 * @example parseRRuleFrequency('INTERVAL=2;FREQ=MONTHLY') → 'MONTHLY'
 * @example parseRRuleFrequency('') → 'YEARLY'
 */
export function parseRRuleFrequency(rrule: string | undefined | null): string {
  if (!rrule) return 'YEARLY';

  const upper = rrule.toUpperCase();
  const match = /FREQ=([A-Z]+)/.exec(upper);
  return match ? match[1] : 'YEARLY';
}

/**
 * Compute a sensible default date window based on RRULE frequency.
 *
 * | Frequency | Window          |
 * |-----------|-----------------|
 * | YEARLY    | now ± 2 years   |
 * | MONTHLY   | now ± 6 months  |
 * | WEEKLY    | now ± 8 weeks   |
 * | DAILY     | now ± 14 days   |
 * | HOURLY    | now ± 12 hours  |
 * | (other)   | now ± 1 year    |
 */
export function computeDefaultWindow(
  rruleFreq: string,
  now: Date = new Date(),
): { from: Date; to: Date } {
  const freq = rruleFreq.toUpperCase();

  switch (freq) {
    case 'YEARLY': {
      const from = new Date(now.getFullYear() - 2, 0, 1);
      const to = new Date(now.getFullYear() + 2, 11, 31, 23, 59, 59, 999);
      return { from, to };
    }
    case 'MONTHLY': {
      const from = new Date(now);
      from.setMonth(from.getMonth() - 6);
      from.setDate(1);
      from.setHours(0, 0, 0, 0);
      const to = new Date(now);
      to.setMonth(to.getMonth() + 6 + 1);
      to.setDate(0); // last day of the target month
      to.setHours(23, 59, 59, 999);
      return { from, to };
    }
    case 'WEEKLY': {
      const from = new Date(now);
      from.setDate(from.getDate() - 8 * 7);
      from.setHours(0, 0, 0, 0);
      const to = new Date(now);
      to.setDate(to.getDate() + 8 * 7);
      to.setHours(23, 59, 59, 999);
      return { from, to };
    }
    case 'DAILY': {
      const from = new Date(now);
      from.setDate(from.getDate() - 14);
      from.setHours(0, 0, 0, 0);
      const to = new Date(now);
      to.setDate(to.getDate() + 14);
      to.setHours(23, 59, 59, 999);
      return { from, to };
    }
    case 'HOURLY': {
      const from = new Date(now);
      from.setHours(from.getHours() - 12, 0, 0, 0);
      const to = new Date(now);
      to.setHours(to.getHours() + 12, 59, 59, 999);
      return { from, to };
    }
    default: {
      const from = new Date(now.getFullYear() - 1, 0, 1);
      const to = new Date(now.getFullYear() + 1, 11, 31, 23, 59, 59, 999);
      return { from, to };
    }
  }
}

/* ------------------------------------------------------------------------- */
/*  Level extraction                                                         */
/* ------------------------------------------------------------------------- */

const FREQ_TO_LEVEL: Record<string, string> = {
  YEARLY: 'year',
  MONTHLY: 'month',
  WEEKLY: 'week',
  DAILY: 'day',
  HOURLY: 'hour',
};

/**
 * Infer a level name from an RRULE frequency string.
 * Falls back to `'year'` when the frequency is unrecognized.
 */
export function inferLevelFromRrule(rrule: string | undefined | null): string {
  const freq = parseRRuleFrequency(rrule);
  return FREQ_TO_LEVEL[freq] ?? 'year';
}

/**
 * Extract the ordered list of hierarchical levels from a timeframe.
 *
 * Walks the `nestedTimeframes` chain, collecting each `timeframeTypeKey`.
 * For a flat timeframe (no nesting) returns a single-element array.
 *
 * @example
 * // Calendar Year: year → quarter → month → week
 * getLevels({ timeframeTypeKey: 'year', nestedTimeframes: [{ timeframeTypeKey: 'quarter', nestedTimeframes: [...] }] })
 * // → ['year', 'quarter', 'month', 'week']
 *
 * // Weekly Sprint: single flat level
 * getLevels({ timeframeTypeKey: 'week', nestedTimeframes: [] })
 * // → ['week']
 */
export function getLevels(timeframe: TimeframeLevelInfo): string[] {
  const rootLevel =
    timeframe.timeframeTypeKey ||
    inferLevelFromRrule(timeframe.scheduleDefinition?.rrule);

  const levels: string[] = [rootLevel];
  let nested = timeframe.nestedTimeframes;

  while (nested && nested.length > 0) {
    levels.push(nested[0].timeframeTypeKey);
    nested = nested[0].nestedTimeframes;
  }

  return levels;
}

/**
 * Return the default level for browsing a timeframe.
 *
 * For hierarchical timeframes (>1 level), returns the second-to-last level
 * (e.g. "month" for year→quarter→month→week). This is the level most commonly
 * useful for period selection.
 *
 * For flat timeframes (1 level), returns the only level.
 */
export function getDefaultLevel(levels: string[]): string {
  if (levels.length <= 1) return levels[0] ?? 'year';
  return levels[levels.length - 2];
}

/* ------------------------------------------------------------------------- */
/*  Date windows                                                             */
/* ------------------------------------------------------------------------- */

export interface DateWindow {
  from: Date;
  to: Date;
  /** Number of months each prev/next navigation step advances. */
  stepMonths: number;
}

/**
 * Compute a default date window and navigation step size for a given level.
 *
 * | Level   | Window              | Step        |
 * |---------|---------------------|-------------|
 * | week    | current quarter     | ± 3 months  |
 * | month   | current year        | ± 12 months |
 * | quarter | current year ± 1    | ± 12 months |
 * | year    | current year ± 5    | ± 60 months |
 */
export function getDateWindow(level: string, now: Date = new Date()): DateWindow {
  const y = now.getFullYear();
  const m = now.getMonth();

  switch (level) {
    case 'week': {
      const quarterStart = Math.floor(m / 3) * 3;
      return {
        from: new Date(y, quarterStart, 1),
        to: endOfMonth(y, quarterStart + 2),
        stepMonths: 3,
      };
    }
    case 'month': {
      return {
        from: new Date(y, 0, 1),
        to: new Date(y, 11, 31, 23, 59, 59, 999),
        stepMonths: 12,
      };
    }
    case 'quarter': {
      return {
        from: new Date(y - 1, 0, 1),
        to: new Date(y + 1, 11, 31, 23, 59, 59, 999),
        stepMonths: 12,
      };
    }
    case 'year': {
      return {
        from: new Date(y - 5, 0, 1),
        to: new Date(y + 5, 11, 31, 23, 59, 59, 999),
        stepMonths: 60,
      };
    }
    default: {
      // Fallback: treat like month
      return {
        from: new Date(y, 0, 1),
        to: new Date(y, 11, 31, 23, 59, 59, 999),
        stepMonths: 12,
      };
    }
  }
}

/** Return the last instant of the given month (year, zero-based month). */
function endOfMonth(year: number, month: number): Date {
  // new Date(y, m+1, 0) gives the last day of month m
  const lastDay = new Date(year, month + 1, 0);
  lastDay.setHours(23, 59, 59, 999);
  return lastDay;
}

/* ------------------------------------------------------------------------- */
/*  Current-period check                                                     */
/* ------------------------------------------------------------------------- */

/**
 * Check whether today (or a provided date) falls within a period's date range.
 * Accepts both ISO strings and Date objects.
 */
export function isCurrentPeriod(
  startDate: string | Date,
  endDate: string | Date,
  now: Date = new Date(),
): boolean {
  const start = typeof startDate === 'string' ? new Date(startDate) : startDate;
  const end = typeof endDate === 'string' ? new Date(endDate) : endDate;
  return now >= start && now <= end;
}
